import { useCallback, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useToast } from '@/shared/contexts/ToastContext'

import {
  createNutritionCustomer,
  draftNutritionCustomer,
  getNutritionCustomer,
  listNutritionRounds,
  patchNutritionCustomer,
  searchNutritionCustomers,
  similarNutritionCustomers,
  startNutritionRound,
  type NutritionCustomer,
  type NutritionCustomerInput,
} from './api'
import { useNutritionStaff } from './useNutritionStaff'

const customerSearchKey = (q: string) =>
  ['nutritionCustomers', 'search', q] as const
const customerKey = (customerId: string) =>
  ['nutritionCustomers', customerId] as const
// カルテの回 (今回と着地の印つき)。今回を始める・消す・段階が進むたびに無効化する
export const nutritionRoundsKey = (customerId: string) =>
  ['nutritionRounds', customerId] as const

// カルテの回 (新しい順。中身の無い器は来ない)。今回は back が決めた印 (current) で読み、画面は選ばない。
export const useNutritionRounds = (customerId: string | null) =>
  useQuery({
    queryKey: nutritionRoundsKey(customerId ?? ''),
    queryFn: () => listNutritionRounds(customerId!),
    enabled: customerId !== null,
  })

// 検索。入力のたびに叩く (back は店舗の中の部分一致で軽い)。前の結果を残して置き換えるので、
// 1 文字ごとに一覧が消えない。
export const useNutritionCustomerSearch = (q: string) =>
  useQuery({
    queryKey: customerSearchKey(q),
    queryFn: () => searchNutritionCustomers(q),
    placeholderData: (previous) => previous,
  })

// 登録の前に出す似たお客様 (ふりがな か 生年月日 が同じ。どちらも空なら叩かない)
export const useSimilarNutritionCustomers = (
  nameKana: string,
  birthDate: string | null
) =>
  useQuery({
    queryKey: ['nutritionCustomers', 'similar', nameKana, birthDate] as const,
    queryFn: () => similarNutritionCustomers(nameKana, birthDate),
    enabled: nameKana.trim() !== '' || birthDate !== null,
    placeholderData: (previous) => previous,
  })

export const useNutritionCustomer = (customerId: string | null) =>
  useQuery({
    queryKey: customerKey(customerId ?? ''),
    queryFn: () => getNutritionCustomer(customerId!),
    enabled: customerId !== null,
  })

const useInvalidateCustomers = () => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: ['nutritionCustomers'] })
}

export const useCreateNutritionCustomer = () => {
  const invalidate = useInvalidateCustomers()
  return useMutation({
    mutationFn: (input: NutritionCustomerInput) =>
      createNutritionCustomer(input),
    onSuccess: () => void invalidate(),
  })
}

// お客様 (マスタ) の保存。お客様の情報 (4 欄・患者 ID) と、出すときの患者 ID の両方がここを通る (書き手は 1 つ)。
// 同じお客様への保存は直列にする (順序が入れ替わると結果が変わる)。応答で手元のお客様を置き換え、検索の一覧は取り直す。
// 記録に写した報告書の欄は触らない (報告書はその時点の文書。1 欄 1 書き手)。
export const useUpdateNutritionCustomer = (customerId: string) => {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateCustomers()
  const { showToast } = useToast()
  return useMutation({
    scope: { id: `nutrition-customer:${customerId}` },
    mutationFn: (input: NutritionCustomerInput) =>
      patchNutritionCustomer(customerId, input),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKey(customerId), customer)
      void invalidate()
    },
    onError: () => showToast('お客様の情報を保存できませんでした', 'error'),
  })
}

// 今回を始める (今回があればその id。無ければ back が作る)。問診票の画像は呼び出し側が記録ができてから送る
// (送れなくても記録はできている)。担当はその PC で前に選んだ人 (名簿に無ければ先頭。名簿が空・読み込み中なら渡さない)。
// 回の一覧 (今回の印) とお客様の並び (最後に記録を始めた日) が変わるので、両方を取り直す。
export const useStartNutritionRound = () => {
  const queryClient = useQueryClient()
  const { sessionStaffId } = useNutritionStaff()
  return useMutation({
    mutationFn: async (customer: Pick<NutritionCustomer, 'id'>) => {
      const { event_id } = await startNutritionRound(
        customer.id,
        sessionStaffId || undefined
      )
      return event_id
    },
    onSuccess: (_, customer) => {
      void queryClient.invalidateQueries({ queryKey: ['nutritionCustomers'] })
      void queryClient.invalidateQueries({
        queryKey: nutritionRoundsKey(customer.id),
      })
    },
  })
}

// 今回を始めた手 (録る = 今回ができた時点で録音を始める・メモ = メモ欄に focus を置く)
export type RoundStartedBy = 'record' | 'memo'

// カルテで今回を始める (最初の 1 手 = 録る・資料を足す・メモを書く)。同じ画面で手が重なっても (資料を続けて選ぶ・
// 録るとメモ) 往復中の 1 本を待ち、記録を 2 つ作らない。往復が終われば次の手はまた back に聞く (back は今回か、
// 使い回せる器を返すので、続けて呼んでも増えない。消した後に始め直すと新しい今回になる)。作れなければ toast を
// 出して投げる。返すのは記録 (録音イベント) の id。
export const useStartRound = (customerId: string) => {
  const { mutateAsync } = useStartNutritionRound()
  const { showToast } = useToast()
  const started = useRef<Promise<string> | null>(null)
  return useCallback(() => {
    if (!started.current) {
      started.current = mutateAsync({ id: customerId })
        .catch((e) => {
          showToast('記録を作れませんでした', 'error')
          throw e
        })
        .finally(() => {
          started.current = null
        })
    }
    return started.current
  }, [customerId, mutateAsync, showToast])
}

export const useDraftNutritionCustomer = () =>
  useMutation({
    mutationFn: (image: Blob) => draftNutritionCustomer(image),
  })
